import { useEffect } from 'react';

import * as yup from 'yup';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';

import i18n from '../../../../../../i18n';
import { BlikAuthorizationScreen } from './BlikAuthorizationScreen';
import { useBlikForm } from '../../../../hooks/use-blik-form';
import { InputField } from '../../../../../InputField/InputField';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { usePaymentState } from '../../../../hooks/use-payment-state';
import { PayButton } from '../../../PaymentForm/components/PayButton/PayButton';
import { PaymentElementContainer } from '../../../PaymentElementContainer/PaymentElementContainer';
import { apmLatencyTracker } from '../../../../../../utils/apm-latency-tracker';
import { getCheckoutEmailSchema } from '../../../../../../utils/checkout-email-validation';
import { BLIK_CODE_LENGTH, isBlikCodeValid } from '../../../../../../utils/blik';

const validationSchema = yup.object().shape({
  email: getCheckoutEmailSchema({
    requiredMessage: i18n.t('checkout.fieldErrors.incompleteEmail'),
    invalidMessage: i18n.t('checkout.fieldErrors.invalidEmail'),
  }),
  code: yup
    .string()
    .test('is-blik-code', i18n.t('checkout.fieldErrors.invalidBlikCode'), (value) =>
      isBlikCodeValid(value || ''),
    ),
});

interface NativeBlikPaymentProps {
  onReady: () => void;
  onSubmit: () => void;
}

export function NativeBlikPayment({ onReady, onSubmit }: NativeBlikPaymentProps) {
  const { checkoutData, selectedPaymentMethod } = useCheckout();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { setFormSubmit, setFormValues, setIsFormValid } = useBlikForm();
  const isBlikAwaitingApproval = usePaymentState((state) => state.isBlikAwaitingApproval);
  const customer = checkoutData?.customer;

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues: {
      email: customer?.email || '',
      code: '',
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Blik', selectedPaymentMethod?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    setTimeout(() => {
      onReady();

      try {
        apmLatencyTracker.endTracking('Blik', selectedPaymentMethod?.id, {
          paymentMethodName: selectedPaymentMethod?.paymentMethodName,
          paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
          provider: 'Stripe',
          type: 'APM',
        });
      } catch (error) {
        console.error('Failed to end APM latency tracking:', error);
      }
    }, 300);
  }, [onReady, selectedPaymentMethod?.id]);

  useEffect(() => {
    setIsFormValid(formik.isValid);
    if (formik.isValid) {
      setFormValues(formik.values);
    }
  }, [formik.isValid, formik.values, setIsFormValid, setFormValues]);

  useEffect(() => {
    setFormSubmit(formik.submitForm);
  }, [formik.submitForm, setFormSubmit]);

  return (
    <>
      {isBlikAwaitingApproval && <BlikAuthorizationScreen />}
      <PaymentElementContainer
        content={
          <FormikProvider value={formik}>
            <Stack spacing={2}>
              <InputField
                name={'email'}
                label={t('email')}
                inputProps={{
                  placeholder: t('emailAddress'),
                }}
              />
              <InputField
                name={'code'}
                label={t('blikCode')}
                inputProps={{
                  placeholder: t('blikCode'),
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                  maxLength: BLIK_CODE_LENGTH,
                  onChange: (e) =>
                    formik.setFieldValue(
                      'code',
                      e.target.value.replace(/\D/g, '').slice(0, BLIK_CODE_LENGTH),
                    ),
                }}
              />
            </Stack>
          </FormikProvider>
        }
      >
        <PayButton onClick={onSubmit} />
      </PaymentElementContainer>
    </>
  );
}
